<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, useAttrs } from 'vue'

defineOptions({
  name: 'CommonImage',
  inheritAttrs: false,
})

const DEFAULT_FALLBACK_IMAGE =
  'https://resources1.interface003.com/web30/2026/07/07/dfb37400-c561-4a6f-8718-2ac0e0d9eca6.svg'
const LAZY_PLACEHOLDER_IMAGE = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs='
const OSS_IMAGE_FORMAT = 'webp'
const OSS_SUPPORTED_SOURCE_EXTENSIONS = new Set([
  'jpg',
  'jpeg',
  'png',
  'bmp',
  'gif',
  'webp',
  'tif',
  'tiff',
  'heic',
  'avif',
])

const props = withDefaults(
  defineProps<{
    src?: string | null
    alt?: string
    width?: string | number
    height?: string | number
    loading?: 'eager' | 'lazy'
    fetchPriority?: 'high' | 'low' | 'auto'
    lazyMargin?: number
    objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down'
    fallbackSrc?: string | boolean
    fallbackScale?: number
    errorTimeoutMs?: number
  }>(),
  {
    src: '',
    alt: '',
    width: undefined,
    height: undefined,
    loading: 'lazy',
    fetchPriority: 'auto',
    objectFit: 'cover',
    fallbackSrc: DEFAULT_FALLBACK_IMAGE,
    fallbackScale: 50,
    errorTimeoutMs: 20000,
  },
)

const emit = defineEmits<{
  error: [event: Event]
  load: [event: Event]
}>()

const appStore = useAppStore()
const attrs = useAttrs()
const hasLoadError = ref(false)
const hasLoaded = ref(false)
const mainImageRef = ref<HTMLImageElement | null>(null)
const shouldLoad = ref(props.loading !== 'lazy')
let errorTimeoutTimer: ReturnType<typeof setTimeout> | null = null
let imageObserver: IntersectionObserver | null = null

const normalizedSrc = computed(() => (typeof props.src === 'string' ? props.src.trim() : ''))
const hasSource = computed(() => Boolean(normalizedSrc.value))
const optimizedSrc = computed(() => {
  const source = normalizedSrc.value
  if (!source) {
    return source
  }

  const hashIndex = source.indexOf('#')
  const hash = hashIndex >= 0 ? source.slice(hashIndex) : ''
  const sourceWithoutHash = hashIndex >= 0 ? source.slice(0, hashIndex) : source
  const queryIndex = sourceWithoutHash.indexOf('?')
  const pathname = queryIndex >= 0 ? sourceWithoutHash.slice(0, queryIndex) : sourceWithoutHash
  const sourceExtension = pathname.match(/\.([^.]+)$/)?.[1]?.toLowerCase()

  if (/\.[^./]+_[^/?#]+$/.test(pathname) || !sourceExtension || !OSS_SUPPORTED_SOURCE_EXTENSIONS.has(sourceExtension)) {
    return source
  }

  const separator = queryIndex >= 0 ? '&' : '?'
  return `${sourceWithoutHash}${separator}x-oss-process=image/format,${OSS_IMAGE_FORMAT}${hash}`
})
const safeFallbackScale = computed(() => Math.min(Math.max(props.fallbackScale, 0), 100))
const normalizeImageSize = (value?: string | number) => {
  if (typeof value === 'number' || (typeof value === 'string' && /^\d+(?:\.\d+)?$/.test(value))) {
    return `${value}px`
  }

  return value
}
const imageWidth = computed(() => normalizeImageSize(props.width))
const imageHeight = computed(() => normalizeImageSize(props.height))
const hasSizeClass = computed(() => {
  if (typeof attrs.class !== 'string') {
    return false
  }

  return /(?:^|\s)(?:size|w|h)-(?:\[|auto|full|screen|\d)/.test(attrs.class)
})
const shouldFillContainer = computed(() => Boolean(imageWidth.value || imageHeight.value || hasSizeClass.value))
const imageObjectFitClass = computed(() => {
  if (typeof attrs.class !== 'string') {
    return ''
  }

  return attrs.class.split(/\s+/).find((className) => className.startsWith('object-')) || ''
})
const showFallback = computed(() => shouldLoad.value && (!hasSource.value || hasLoadError.value))
const showFallbackImage = computed(() => showFallback.value || (props.loading === 'lazy' && !hasLoaded.value))
const showMainImage = computed(
  () => hasSource.value && !hasLoadError.value && (props.loading !== 'lazy' || hasLoaded.value),
)
const fallbackImageClass = computed(() => (showFallbackImage.value ? 'is-visible' : 'is-hidden'))
const fallbackImageSrc = computed(() => {
  if (props.fallbackSrc === false) {
    return LAZY_PLACEHOLDER_IMAGE
  }

  return typeof props.fallbackSrc === 'string' ? props.fallbackSrc : DEFAULT_FALLBACK_IMAGE
})

const syncLoadedState = () => {
  if (!import.meta.client || !shouldLoad.value || hasLoadError.value) {
    return
  }

  const imageEl = mainImageRef.value

  if (!imageEl || !hasSource.value) {
    return
  }

  if (imageEl.complete && imageEl.naturalWidth > 0) {
    clearErrorTimeout()
    hasLoaded.value = true
  }
}

function clearErrorTimeout() {
  if (!errorTimeoutTimer) {
    return
  }

  clearTimeout(errorTimeoutTimer)
  errorTimeoutTimer = null
}

function startErrorTimeout() {
  clearErrorTimeout()

  if (!import.meta.client || !shouldLoad.value || props.errorTimeoutMs <= 0 || !normalizedSrc.value) {
    return
  }

  errorTimeoutTimer = setTimeout(() => {
    if (hasLoadError.value) {
      return
    }

    hasLoadError.value = true
    emit('error', new Event('error'))
  }, props.errorTimeoutMs)
}

watch(
  normalizedSrc,
  () => {
    hasLoadError.value = false
    hasLoaded.value = false
    startErrorTimeout()

    if (import.meta.client) {
      void nextTick(syncLoadedState)
    }
  },
  { immediate: true },
)

watch(shouldLoad, (value) => {
  if (!value) {
    return
  }

  startErrorTimeout()
  if (import.meta.client) {
    void nextTick(syncLoadedState)
  }
})

const handleFallbackError = (event: Event) => {
  emit('error', event)
}

const handleImageError = (event: Event) => {
  clearErrorTimeout()
  hasLoadError.value = true
  hasLoaded.value = false
  emit('error', event)
}

const handleImageLoad = (event: Event) => {
  if (!shouldLoad.value || !hasSource.value) {
    return
  }

  clearErrorTimeout()
  hasLoaded.value = true
  emit('load', event)
}

onMounted(() => {
  if (props.loading === 'lazy' && 'IntersectionObserver' in window && mainImageRef.value) {
    imageObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) {
          return
        }

        shouldLoad.value = true
        imageObserver?.disconnect()
        imageObserver = null
      },
      {
        rootMargin: `${props.lazyMargin ?? appStore.hydrateOnVisibleRootMargin}px 0px`,
        threshold: 0,
      },
    )
    imageObserver.observe(mainImageRef.value)
  } else {
    shouldLoad.value = true
  }

  syncLoadedState()
})

onBeforeUnmount(() => {
  clearErrorTimeout()
  imageObserver?.disconnect()
  imageObserver = null
})
</script>

<template>
  <div
    v-bind="$attrs"
    class="common-image"
    :class="{ 'common-image--sized': shouldFillContainer }"
    :style="{ width: imageWidth, height: imageHeight }"
  >
    <img
      :src="fallbackImageSrc"
      :alt="alt"
      data-allow-mismatch="class"
      class="image-layer image-fallback"
      :class="fallbackImageClass === 'is-visible' ? 'visible' : 'invisible'"
      :style="{ width: `${safeFallbackScale}%`, maxHeight: `${safeFallbackScale}%` }"
      aria-hidden="true"
      @error="handleFallbackError"
    />
    <img
      ref="mainImageRef"
      :src="shouldLoad ? optimizedSrc : LAZY_PLACEHOLDER_IMAGE"
      :alt="alt"
      data-allow-mismatch="class"
      :width="width"
      :height="height"
      class="image-layer main-image"
      :class="[
        imageObjectFitClass,
        {
          visible: showMainImage,
          invisible: !showMainImage,
          'object-cover': props.objectFit === 'cover' && !imageObjectFitClass,
          'object-contain': props.objectFit === 'contain' && !imageObjectFitClass,
          'object-fill': props.objectFit === 'fill' && !imageObjectFitClass,
          'object-none': props.objectFit === 'none' && !imageObjectFitClass,
          'object-scale-down': props.objectFit === 'scale-down' && !imageObjectFitClass,
        },
      ]"
      :style="{
        objectFit,
      }"
      :loading="loading"
      :fetchpriority="fetchPriority"
      @error="handleImageError"
      @load="handleImageLoad"
    />
  </div>
</template>

<style scoped>
@layer components {
  :where(.common-image) {
    position: relative;
  }
}

:where(.common-image) {
  display: inline-block;
  overflow: hidden;
  vertical-align: middle;
}

.image-layer {
  display: block;
}

.image-fallback {
  position: absolute;
  inset: 0;
  z-index: 0;
  margin: auto;
  object-fit: contain;
  pointer-events: none;
}

.main-image {
  position: relative;
  z-index: 1;
  width: auto;
  height: auto;
  max-width: 100%;
}

.common-image--sized .main-image {
  width: 100%;
  height: 100%;
}
</style>
